// Ren beregning for bedriftsinfo fra Brønnøysund (CRM-3.1).
//
// IDENTISK KOPI i supabase/functions/_shared/brreg-calc.ts (edge functions kan
// ikke importere fra src/). Endrer du logikken her, endre den der også.

export interface RoleLike {
  role_code: string;
  person_name: string;
}

/** Vekst som utløser signalet «sterk vekst»: omsetning minst 25 % opp (1,25 ×). */
export const STRONG_GROWTH_FACTOR = 1.25;

/** Jubileer (år siden stiftelse) som gir signal. */
export const ANNIVERSARY_YEARS = [10, 20, 25, 30, 40, 50] as const;

/** Prosentvis vekst i omsetning, eller null når grunnlaget mangler (forrige år ≤ 0). */
export function revenueGrowthPct(cur: number | null | undefined, prev: number | null | undefined): number | null {
  if (cur == null || prev == null || !(prev > 0)) return null;
  return ((cur - prev) / prev) * 100;
}

/** Omsetningen er minst STRONG_GROWTH_FACTOR ganger forrige år. */
export function isStrongGrowth(cur: number | null | undefined, prev: number | null | undefined): boolean {
  if (cur == null || prev == null || !(prev > 0)) return false;
  return cur >= prev * STRONG_GROWTH_FACTOR;
}

function toUtcDay(date: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

/** Stiftelsesdato pluss n år; 29. februar havner på 28. februar i år uten skuddår. */
function addYears(base: Date, years: number): Date {
  const y = base.getUTCFullYear() + years;
  const m = base.getUTCMonth();
  const d = base.getUTCDate();
  const candidate = new Date(Date.UTC(y, m, d));
  return candidate.getUTCMonth() === m ? candidate : new Date(Date.UTC(y, m + 1, 0));
}

/**
 * Jubileet (10/20/25/30/40/50 år) som inntreffer fra og med i dag og innen
 * `days` dager, eller null. `date` er jubileumsdagen (YYYY-MM-DD).
 */
export function anniversaryWithin(
  founded: string | null | undefined,
  now: Date,
  days: number,
): { years: number; date: string } | null {
  const base = founded ? toUtcDay(founded) : null;
  if (!base) return null;
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const limit = new Date(today.getTime() + days * 86_400_000);
  for (const years of ANNIVERSARY_YEARS) {
    const at = addYears(base, years);
    if (at >= today && at <= limit) return { years, date: at.toISOString().slice(0, 10) };
  }
  return null;
}

/** Daglig leder (DAGL), ellers styrets leder (LEDE). */
export function leaderFromRoles<T extends RoleLike>(roles: readonly T[] | null | undefined): T | null {
  if (!roles) return null;
  return roles.find((r) => r.role_code === "DAGL") ?? roles.find((r) => r.role_code === "LEDE") ?? null;
}

/** Styrets leder (LEDE). */
export function chairFromRoles<T extends RoleLike>(roles: readonly T[] | null | undefined): T | null {
  return roles?.find((r) => r.role_code === "LEDE") ?? null;
}

/** Sammenligningsnøkkel for personnavn: små bokstaver, kollapset mellomrom. */
export function normalizePersonName(name: string | null | undefined): string {
  return (name ?? "").toLocaleLowerCase("nb-NO").replace(/\s+/g, " ").trim();
}
